import { store } from "../core/store";
import { socket } from "../core/socket";
import type { ChatMessageDTO } from "../../shared/types";

const SCROLL_BOTTOM_THRESHOLD = 40;

export function initChat(): void {
  const messagesEl = document.getElementById("chat-messages")!;
  const form = document.getElementById("chat-form") as HTMLFormElement;
  const input = document.getElementById("chat-input") as HTMLInputElement;

  let autoScroll = true;
  // Identity check, not a length check: `chatMessages` isn't always grown by a
  // plain append - a "chat:history" full-sync (sent to a freshly joined/reconnected
  // socket right after it also received the live "chat:message" broadcast for its
  // own join announcement) *replaces* the array wholesale with a server-authoritative
  // list that already contains that same message. An index/length-based diff would
  // re-render that one message a second time while never rendering the earlier
  // history it missed, so a full rebuild keyed off array identity is what's actually
  // correct here - cheap to bail out of on every unrelated store update (most of
  // them), and correct (not just fast) on the ones that do touch chat.
  let lastMessages: ChatMessageDTO[] = [];

  messagesEl.addEventListener("scroll", () => {
    const distanceFromBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight;
    autoScroll = distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;
  });

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    socket.emit("chat:send", text);
    input.value = "";
    autoScroll = true;
  });

  store.subscribe((state) => {
    if (state.chatMessages === lastMessages) return;
    const previous = lastMessages;
    lastMessages = state.chatMessages;

    // The common case - a live message appended (possibly with the oldest
    // dropped off the front at the 500 cap) - only touches the rows that
    // actually changed, instead of rebuilding up to 500 rows per message. Any
    // other change (a chat:history full-sync with fresh objects, a wipe) still
    // takes the full rebuild below, by the same identity reasoning as above.
    if (appendIncrementally(messagesEl, previous, lastMessages)) {
      if (autoScroll) messagesEl.scrollTop = messagesEl.scrollHeight;
      return;
    }

    messagesEl.innerHTML = "";
    const fragment = document.createDocumentFragment();
    for (const message of lastMessages) {
      fragment.appendChild(renderMessage(message));
    }
    messagesEl.appendChild(fragment);

    if (autoScroll) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
  });
}

/** Applies `next` on top of the rows already rendered for `previous`, provided
 * `next` is just `previous` with some messages dropped off the front and/or new
 * ones appended (compared by object identity). Returns false - touching
 * nothing - if that isn't the case. */
function appendIncrementally(container: HTMLElement, previous: ChatMessageDTO[], next: ChatMessageDTO[]): boolean {
  if (previous.length === 0 || next.length === 0) return false;
  if (container.childElementCount !== previous.length) return false;
  const dropped = previous.indexOf(next[0]!);
  if (dropped < 0) return false;
  const kept = previous.length - dropped;
  if (kept > next.length) return false;
  for (let i = 0; i < kept; i++) {
    if (next[i] !== previous[i + dropped]) return false;
  }
  for (let i = 0; i < dropped; i++) container.firstElementChild?.remove();
  const fragment = document.createDocumentFragment();
  for (let i = kept; i < next.length; i++) fragment.appendChild(renderMessage(next[i]!));
  container.appendChild(fragment);
  return true;
}

function renderMessage(message: ChatMessageDTO): HTMLElement {
  const row = document.createElement("div");
  row.className = `chat-message chat-message--${message.type}`;

  if (message.type === "system" || message.type === "announcement") {
    row.textContent = message.text;
    return row;
  }

  if (message.playerName) {
    const author = document.createElement("span");
    author.className = "chat-author";
    author.textContent = message.playerName + ": ";
    if (message.playerColor) author.style.color = message.playerColor;
    row.appendChild(author);
  }

  const isGuessFeedback =
    message.type === "guess-close" || message.type === "guess-partial" || message.type === "guess-correct";
  const text = document.createElement(isGuessFeedback ? "b" : "span");
  text.className = "chat-text";
  if (message.type === "guess-close") {
    text.textContent = `${message.text} - CLOSE!`;
  } else if (message.type === "guess-partial") {
    text.textContent = `${message.text} - Partially correct!`;
  } else {
    text.textContent = message.text;
  }
  row.appendChild(text);
  return row;
}
